import { useMemo, useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import RatingStars from "@/components/base/RatingStars";
import { storeCuisines, stores, type StoreCuisine } from "@/mocks/stores";

type FilterId = (typeof storeCuisines)[number]["id"];

export default function StoreSection() {
  const [active, setActive] = useState<FilterId>("all");

  const filtered = useMemo(() => {
    if (active === "all") return stores;
    return stores.filter((store) => store.cuisine === (active as StoreCuisine));
  }, [active]);

  return (
    <section id="stores" className="w-full bg-background-100 py-16 md:py-20">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="探店实录"
            title="实地走访，把排队、营业时间和招牌菜都记下来"
            description="我们按菜系整理探店记录，标注人均价格、营业时段与最值得点的那一道，方便你安排行程。"
          />
          <div className="flex flex-wrap gap-2">
            {storeCuisines.map((item) => {
              const isActive = active === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActive(item.id)}
                  aria-pressed={isActive}
                  className={`whitespace-nowrap rounded-full border px-3.5 py-2 text-xs font-medium transition-colors md:text-sm ${
                    isActive
                      ? "border-secondary-500 bg-secondary-500 text-background-50"
                      : "border-background-300 bg-background-50 text-foreground-600 hover:border-secondary-400 hover:text-foreground-950"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="mt-12 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-12 text-center text-sm text-foreground-500">
            暂无该菜系的探店记录，换个分类看看。
          </p>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-2">
            {filtered.map((store) => (
              <article
                key={store.id}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 sm:flex-row"
              >
                <div className="relative h-48 w-full shrink-0 overflow-hidden sm:h-auto sm:w-52">
                  <img
                    src={store.image}
                    alt={`${store.name}（${store.cuisineLabel}）- 探店实录配图`}
                    title={`${store.name} 探店图片`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-background-50/95 px-2.5 py-1 text-[11px] font-semibold text-secondary-900">
                    {store.cuisineLabel}
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-3 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-heading text-lg font-bold text-foreground-950">{store.name}</h3>
                    <span className="shrink-0 whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-semibold text-accent-900">
                      {store.price}
                    </span>
                  </div>
                  <RatingStars value={store.rating} />
                  <ul className="flex flex-col gap-1.5 text-xs text-foreground-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-map-pin-line mt-0.5 text-primary-600" aria-hidden="true" />
                      {store.district}
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-star-smile-line mt-0.5 text-primary-600" aria-hidden="true" />
                      招牌：{store.signature}
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-time-line mt-0.5 text-primary-600" aria-hidden="true" />
                      营业：{store.openHours} · {store.reviewCount} 条记录
                    </li>
                  </ul>
                  <p className="mt-auto rounded-md bg-secondary-50 px-3 py-2.5 text-xs leading-relaxed text-secondary-900">
                    <i className="ri-information-line mr-1" aria-hidden="true" />
                    {store.note}
                  </p>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}